import type { FilterState, GraphNode, GraphRelation } from '../data/types';

const normalize = (value: string): string => value.toLocaleLowerCase('pl-PL').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

function year(value?: string): number | undefined {
  if (!value) return undefined;
  const match = value.match(/\d{4}/);
  return match ? Number(match[0]) : undefined;
}

function overlaps(node: GraphNode, filters: FilterState): boolean {
  const from = year(filters.from);
  const to = year(filters.to);
  if (from === undefined && to === undefined) return true;
  const start = year(node.dateRange?.start) ?? -Infinity;
  const end = year(node.dateRange?.end) ?? start;
  return (from === undefined || end >= from) && (to === undefined || start <= to);
}

export function filterNodes(nodes: GraphNode[], filters: FilterState): GraphNode[] {
  const query = normalize(filters.search.trim());
  return nodes.filter((node) => {
    if (filters.nodeTypes.length > 0 && !filters.nodeTypes.includes(node.type)) return false;
    if (filters.confidence.length > 0 && !filters.confidence.includes(node.confidence)) return false;
    if (filters.sourceIds.length > 0 && !filters.sourceIds.some((id) => node.sourceIds.includes(id))) return false;
    if (filters.mediaOnly && (!node.mediaIds || node.mediaIds.length === 0)) return false;
    if (!overlaps(node, filters)) return false;
    if (!query) return true;
    const haystack = normalize([node.title, node.subtitle, node.summary, node.curatorialNote, ...(node.aliases ?? []), ...node.tags].join(' '));
    return haystack.includes(query);
  });
}

export function filterRelations(relations: GraphRelation[], nodeIds: Set<string>, filters: FilterState): GraphRelation[] {
  return relations.filter((relation) => {
    if (!nodeIds.has(relation.from) || !nodeIds.has(relation.to)) return false;
    if (filters.relationTypes.length > 0 && !filters.relationTypes.includes(relation.type)) return false;
    return true;
  });
}

export function contextFor(nodes: GraphNode[], relations: GraphRelation[], coreIds: string[], limit = 28): GraphNode[] {
  const coreSet = new Set(coreIds);
  const contextIds = new Set(coreIds);
  for (const relation of relations) {
    if (coreSet.has(relation.from)) contextIds.add(relation.to);
    if (coreSet.has(relation.to)) contextIds.add(relation.from);
  }
  const selected = nodes.filter((node) => contextIds.has(node.id));
  return selected.slice(0, limit);
}

export function dateLabel(node: GraphNode): string {
  if (!node.dateRange) return 'data nieustalona';
  if (node.dateRange.label) return node.dateRange.label;
  if (node.dateRange.start && node.dateRange.end && node.dateRange.start !== node.dateRange.end) return `${node.dateRange.start} — ${node.dateRange.end}`;
  return node.dateRange.start ?? node.dateRange.end ?? 'data nieustalona';
}

